import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCurrentUser, useIsInitialized, useIsSignedIn, useSignOut } from '@coinbase/cdp-hooks';
import { clusterApiUrl, Connection, PublicKey } from '@solana/web3.js';
import { useConfig } from 'apps/cdp-web-demo/app/(main)/ConfigContext';
import { isEvmAddress } from 'apps/cdp-web-demo/app/(main)/utils/isEvmAddress';
import { AccountCreationType } from 'apps/cdp-web-demo/app/types';
import { createPublicClient, http } from 'viem';
import { baseSepolia } from 'viem/chains';

type LoggedInView = 'link-profile' | 'export-wallet' | 'add-mfa' | null;

type UserContextType = {
  accountCreationType: AccountCreationType;
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  isLoggedIn: boolean;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  isInitialized: boolean;
  loggedInView: LoggedInView;
  setLoggedInView: Dispatch<SetStateAction<LoggedInView>>;
  userAddress: string | undefined;
  balance: bigint | undefined;
  isPendingBalance: boolean;
  isBalanceInitialized: boolean;
  onSignInSuccess: () => void;
  onSignOutSuccess: () => void;
};

type UserProviderProps = {
  children: ReactNode;
};

const baseSepoliaClient = createPublicClient({
  chain: baseSepolia,
  transport: http(),
});

const solanaDevnetConnection = new Connection(clusterApiUrl('devnet'));

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: UserProviderProps) {
  const [isModalVisible, setIsModalVisible] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedInView, setLoggedInView] = useState<LoggedInView>(null);
  const { isInitialized } = useIsInitialized();
  const { isSignedIn } = useIsSignedIn();
  const { currentUser } = useCurrentUser();
  const { signOut } = useSignOut();
  const initialMount = useRef(true);
  const [isPendingBalance, setIsPendingBalance] = useState(false);
  const [balance, setBalance] = useState<bigint | undefined>(undefined);
  const isBalanceInitialized = useRef(false);
  const { state } = useConfig();
  const {
    ethereum: { createOnLogin: ethereumCreateOnLogin } = {},
    solana: { createOnLogin: solanaCreateOnLogin } = {},
  } = state.config;

  const accountCreationType = useMemo(() => {
    if (solanaCreateOnLogin) {
      return 'solana';
    }
    if (ethereumCreateOnLogin === 'smart') {
      return 'evm-smart';
    }
    return 'evm-eoa';
  }, [ethereumCreateOnLogin, solanaCreateOnLogin]);

  const { evmEoaAddress, evmSmartAddress, solanaAddress } = useMemo(() => {
    return {
      evmEoaAddress: currentUser?.evmAccounts?.[0],
      evmSmartAddress: currentUser?.evmSmartAccounts?.[0],
      solanaAddress: currentUser?.solanaAccounts?.[0],
    };
  }, [currentUser]);

  const userAddress = useMemo(() => {
    if (accountCreationType === 'solana') {
      return solanaAddress;
    }

    return accountCreationType === 'evm-smart' ? evmSmartAddress : evmEoaAddress;
  }, [accountCreationType, evmSmartAddress, evmEoaAddress, solanaAddress]);

  const getBalance = useCallback(async () => {
    if (!userAddress) {
      return;
    }
    try {
      setIsPendingBalance(true);
      if (accountCreationType === 'solana') {
        // Get Solana balance in lamports
        const lamports = await solanaDevnetConnection.getBalance(new PublicKey(userAddress));
        setBalance(BigInt(lamports));
      }
      if (accountCreationType !== 'solana' && isEvmAddress(userAddress)) {
        // Get EVM balance in wei
        const weiBalance = await baseSepoliaClient.getBalance({
          address: userAddress,
        });
        setBalance(weiBalance);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setBalance(undefined);
    } finally {
      setIsPendingBalance(false);
      isBalanceInitialized.current = true;
    }
  }, [userAddress, accountCreationType]);

  const onSignInSuccess = useCallback(() => {
    setTimeout(() => {
      setIsModalVisible(false);
    }, 300);
    setTimeout(() => {
      setIsLoggedIn(true);
    }, 500);
  }, [setIsLoggedIn, setIsModalVisible]);

  const onSignOutSuccess = useCallback(() => {
    setIsModalVisible(true);
    setIsLoggedIn(false);
  }, [setIsLoggedIn, setIsModalVisible]);

  const value = useMemo(() => {
    return {
      accountCreationType,
      isModalVisible,
      setIsModalVisible,
      isLoggedIn,
      setIsLoggedIn,
      isInitialized,
      userAddress,
      balance,
      isPendingBalance,
      isBalanceInitialized: isBalanceInitialized.current,
      onSignInSuccess,
      onSignOutSuccess,
      loggedInView,
      setLoggedInView,
    } satisfies UserContextType;
  }, [
    accountCreationType,
    isModalVisible,
    setIsModalVisible,
    isLoggedIn,
    setIsLoggedIn,
    loggedInView,
    setLoggedInView,
    isInitialized,
    userAddress,
    balance,
    isPendingBalance,
    isBalanceInitialized,
    onSignInSuccess,
    onSignOutSuccess,
  ]);

  useEffect(() => {
    if (!initialMount.current || !isInitialized) {
      return;
    }
    if (isSignedIn) {
      if (!userAddress) {
        // if the user doesn't have an address for the selected account creation type, log them out
        void signOut();
        onSignOutSuccess();
        return;
      }
      setIsLoggedIn(true);
    }
    initialMount.current = false;
  }, [isInitialized, setIsLoggedIn, isSignedIn, userAddress, onSignOutSuccess, signOut]);

  useEffect(() => {
    if (!userAddress) {
      return undefined;
    }
    void getBalance();
    const interval = setInterval(() => void getBalance(), 500);
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [userAddress, getBalance]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
